import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Mail,
  Phone,
  Building2,
  User as UserIcon,
  Sparkles,
  Clock,
  ChevronRight,
  MessageSquareText,
  Activity as ActivityIcon,
  Bell,
  CheckSquare,
  Flag,
  Target,
  Plus,
  Pencil,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import ConversationLogDialog from "@/components/ConversationLogDialog";
import ChallengeDialog from "@/components/ChallengeDialog";
import ContactEditDialog from "@/components/ContactEditDialog";
import { PrimaryButton } from "@/components/Shared";
import { formatDeDate } from "@/lib/dateFormat";

const AMPEL = {
  gruen: {
    dot: "bg-emerald-500",
    ring: "ring-emerald-200",
    panel: "bg-emerald-50 border-emerald-200",
    label: "Grün",
  },
  gelb: {
    dot: "bg-amber-400",
    ring: "ring-amber-200",
    panel: "bg-amber-50 border-amber-200",
    label: "Gelb",
  },
  rot: {
    dot: "bg-rose-500",
    ring: "ring-rose-200",
    panel: "bg-rose-50 border-rose-200",
    label: "Rot",
  },
};

const CHALLENGE_STATUS = {
  aktiv: { label: "Diese Woche aktiv", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  wartet: { label: "Wartet auf andere", color: "bg-amber-50 text-amber-700 border-amber-200" },
  ruhend: { label: "Bewusst ruhen", color: "bg-gray-100 text-gray-600 border-gray-200" },
  abgeschlossen: { label: "Abgeschlossen", color: "bg-blue-50 text-blue-700 border-blue-200" },
};

const PRIO = {
  hoch: { label: "Hoch", color: "text-rose-700" },
  mittel: { label: "Mittel", color: "text-amber-700" },
  niedrig: { label: "Niedrig", color: "text-gray-500" },
};

const ART_LABEL = {
  persoenlich: "Persönlich",
  telefon: "Telefon",
  email: "E-Mail",
  nachricht: "Nachricht",
};

const KIND_META = {
  conversation: { icon: MessageSquareText, color: "text-emerald-700 bg-emerald-50", label: "Gespräch" },
  activity: { icon: ActivityIcon, color: "text-blue-700 bg-blue-50", label: "Aktivität" },
  note: { icon: MessageSquareText, color: "text-amber-700 bg-amber-50", label: "Notiz" },
  followup: { icon: Bell, color: "text-rose-700 bg-rose-50", label: "Wiedervorlage" },
  task: { icon: CheckSquare, color: "text-gray-700 bg-gray-100", label: "Aufgabe" },
};

function fmt(d) {
  return formatDeDate(d);
}
function fmtDateTime(d) {
  // NORA rule: never show a time in the normal UI, only the human date.
  return formatDeDate(d);
}

function Section({ title, children, testid }) {
  return (
    <div className="crm-card p-5" data-testid={testid}>
      <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-3">{title}</div>
      {children}
    </div>
  );
}

function EmptyLine({ children }) {
  return <div className="text-sm text-gray-400 italic">{children}</div>;
}

export default function ContactDetail() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [convOpen, setConvOpen] = useState(false);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [showOtherChallenges, setShowOtherChallenges] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["contact-dossier", contactId],
    queryFn: async () => (await api.get(`/contacts/${contactId}/dossier`)).data,
    enabled: !!contactId,
  });

  const ampelMut = useMutation({
    mutationFn: async ({ ampel_status, ampel_grund }) => {
      const payload = {
        ampel_status,
        ampel_datum: new Date().toISOString(),
      };
      if (ampel_grund !== undefined) payload.ampel_grund = ampel_grund;
      return (await api.put(`/contacts/${contactId}`, payload)).data;
    },
    onSuccess: () => {
      toast.success("Ampel gespeichert");
      qc.invalidateQueries({ queryKey: ["contact-dossier", contactId] });
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  if (isLoading || !data) {
    return (
      <div className="p-8 text-sm text-gray-500" data-testid="contact-detail-loading">
        Lädt Personenakte…
      </div>
    );
  }

  const c = data.contact;
  const ampel = AMPEL[c.ampel_status] || AMPEL.gruen;

  const activeChallenges = data.challenges_active || [];
  const otherChallenges = data.challenges_other || [];

  return (
    <div data-testid="contact-detail-page" className="space-y-6">
      {/* Breadcrumb + back */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button
          onClick={() => navigate("/contacts")}
          className="inline-flex items-center gap-1 hover:text-gray-900"
          data-testid="contact-detail-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Personen
        </button>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-gray-700 font-medium">
          {[c.salutation, c.title, c.first_name, c.last_name].filter(Boolean).join(" ")}
        </span>
      </div>

      {/* Name header + primary actions */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1
            className="font-display font-bold tracking-tight text-2xl md:text-3xl text-gray-900"
            data-testid="contact-detail-name"
          >
            {[c.salutation, c.title, c.first_name, c.last_name].filter(Boolean).join(" ")}
          </h1>
          <div className="mt-1 text-sm text-gray-500 flex flex-wrap items-center gap-3">
            {c.position ? <span>{c.position}</span> : null}
            {c.company_name ? (
              <span className="inline-flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5" /> {c.company_name}
              </span>
            ) : null}
            {c.email ? (
              <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-gray-900">
                <Mail className="w-3.5 h-3.5" /> {c.email}
              </a>
            ) : null}
            {c.phone ? (
              <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-gray-900">
                <Phone className="w-3.5 h-3.5" /> {c.phone}
              </a>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setEditOpen(true)}
            data-testid="contact-detail-edit-btn"
          >
            <Pencil className="w-4 h-4 mr-1.5" /> Person bearbeiten
          </Button>
          <PrimaryButton onClick={() => setConvOpen(true)} data-testid="contact-detail-log-conv-btn">
            <MessageSquareText className="w-4 h-4 mr-1" /> Gespräch erfassen
          </PrimaryButton>
        </div>
      </div>

      {/* --- 4-Panel Kopfzeile (above the fold) --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="contact-detail-headline">
        {/* 1. Ampelstatus */}
        <div
          className={`rounded-xl border p-5 ring-1 ${ampel.panel} ${ampel.ring}`}
          data-testid="contact-detail-panel-ampel"
        >
          <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-3">
            Ampelstatus
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-block w-4 h-4 rounded-full ${ampel.dot}`} />
            <div className="text-lg font-semibold text-gray-900">{ampel.label}</div>
          </div>
          {c.ampel_datum ? (
            <div className="text-xs text-gray-500 mt-2">Entschieden am {fmt(c.ampel_datum)}</div>
          ) : null}
          {c.ampel_grund ? (
            <p className="text-sm text-gray-700 mt-2 leading-relaxed">{c.ampel_grund}</p>
          ) : (
            <EmptyLine>Kein Grund hinterlegt.</EmptyLine>
          )}
          <div className="mt-3">
            <Select
              value={c.ampel_status || "gruen"}
              onValueChange={(v) => ampelMut.mutate({ ampel_status: v })}
            >
              <SelectTrigger className="h-8 text-xs" data-testid="contact-detail-ampel-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gruen">Auf Grün setzen</SelectItem>
                <SelectItem value="gelb">Auf Gelb setzen</SelectItem>
                <SelectItem value="rot">Auf Rot setzen</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* 2. Persönlicher Gesprächsanker */}
        <div className="rounded-xl border border-gray-200 bg-white p-5" data-testid="contact-detail-panel-anker">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Gesprächsanker
          </div>
          <div className="space-y-3 text-sm">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-0.5">Wie kennengelernt?</div>
              {c.anker_kennengelernt ? (
                <p className="text-gray-800 leading-snug" data-testid="contact-detail-anker-kennengelernt">{c.anker_kennengelernt}</p>
              ) : <EmptyLine>Noch nichts hinterlegt.</EmptyLine>}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-0.5">Gemeinsamer Moment</div>
              {c.anker_gemeinsamer_moment ? (
                <p className="text-gray-800 leading-snug" data-testid="contact-detail-anker-moment">{c.anker_gemeinsamer_moment}</p>
              ) : <EmptyLine>Noch nichts hinterlegt.</EmptyLine>}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-0.5">Persönliche Verbindung</div>
              {c.anker_persoenliche_verbindung ? (
                <p className="text-gray-800 leading-snug" data-testid="contact-detail-anker-verbindung">{c.anker_persoenliche_verbindung}</p>
              ) : <EmptyLine>Noch nichts hinterlegt.</EmptyLine>}
            </div>
          </div>
        </div>

        {/* 3. Letzter Kontakt */}
        <div className="rounded-xl border border-gray-200 bg-white p-5" data-testid="contact-detail-panel-last">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Letzter Kontakt
          </div>
          {c.letzter_kontakt_datum ? (
            <>
              <div className="text-lg font-semibold text-gray-900" data-testid="contact-detail-last-date">
                {fmt(c.letzter_kontakt_datum)}
              </div>
              {c.letzter_kontakt_art ? (
                <div className="text-xs text-gray-500 mt-0.5">{c.letzter_kontakt_art}</div>
              ) : null}
              {c.letzter_kontakt_zusammenfassung ? (
                <p className="text-sm text-gray-700 mt-2 leading-relaxed line-clamp-4">
                  {c.letzter_kontakt_zusammenfassung}
                </p>
              ) : null}
            </>
          ) : (
            <EmptyLine>Noch kein Gespräch festgehalten.</EmptyLine>
          )}
        </div>

        {/* 4. Nächster Schritt / offene Zusage */}
        <div className="rounded-xl border border-gray-200 bg-white p-5" data-testid="contact-detail-panel-next">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-1.5">
            <Flag className="w-3.5 h-3.5" /> Nächster Schritt
          </div>
          {c.naechster_schritt ? (
            <p className="text-sm text-gray-900 leading-relaxed" data-testid="contact-detail-next-step">
              {c.naechster_schritt}
            </p>
          ) : (
            <EmptyLine>Kein nächster Schritt hinterlegt.</EmptyLine>
          )}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-0.5">Nächster Termin / Wiedervorlage</div>
            {c.naechster_termin ? (
              <div className="text-sm text-gray-800 font-medium" data-testid="contact-detail-next-date">
                {fmtDateTime(c.naechster_termin)}
              </div>
            ) : (
              <EmptyLine>Noch nicht terminiert.</EmptyLine>
            )}
          </div>
          <FollowUpLink
            openFu={data.open_followup}
            hasNext={Boolean(c.naechster_schritt && c.naechster_termin)}
          />
        </div>
      </div>

      {/* Below the fold: Tabs */}
      <Tabs defaultValue="challenges" className="w-full">
        <TabsList className="bg-white border border-gray-200 rounded-md p-1 flex flex-wrap gap-1 h-auto">
          <TabsTrigger value="challenges" data-testid="contact-detail-tab-challenges">
            <Target className="w-3.5 h-3.5 mr-1" /> Herausforderungen ({activeChallenges.length})
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="contact-detail-tab-history">
            <Clock className="w-3.5 h-3.5 mr-1" /> Historie ({data.history.length})
          </TabsTrigger>
          <TabsTrigger value="details" data-testid="contact-detail-tab-details">
            <UserIcon className="w-3.5 h-3.5 mr-1" /> Details
          </TabsTrigger>
        </TabsList>

        {/* Herausforderungen */}
        <TabsContent value="challenges" className="mt-4 space-y-4" data-testid="contact-detail-content-challenges">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {activeChallenges.length === 0
                ? "Keine aktiven Herausforderungen."
                : `${activeChallenges.length} aktive Herausforderung${activeChallenges.length === 1 ? "" : "en"}`}
            </div>
            <div className="flex items-center gap-2">
              {otherChallenges.length > 0 ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowOtherChallenges((v) => !v)}
                  data-testid="contact-detail-toggle-other-challenges"
                >
                  {showOtherChallenges ? "Nur aktive anzeigen" : `Ruhende/abgeschlossene zeigen (${otherChallenges.length})`}
                </Button>
              ) : null}
              <Button size="sm" onClick={() => setChallengeOpen(true)} className="bg-black text-white hover:bg-gray-800" data-testid="contact-detail-new-challenge-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Herausforderung erfassen
              </Button>
            </div>
          </div>

          {activeChallenges.length === 0 && !showOtherChallenges ? (
            <div className="crm-card p-8 text-center text-sm text-gray-500">
              Noch keine aktiven Herausforderungen für {c.first_name}.
            </div>
          ) : (
            <div className="space-y-3">
              {activeChallenges.map((ch) => (
                <ChallengeCard key={ch.id} challenge={ch} />
              ))}
              {showOtherChallenges
                ? otherChallenges.map((ch) => <ChallengeCard key={ch.id} challenge={ch} />)
                : null}
            </div>
          )}
        </TabsContent>

        {/* Historie */}
        <TabsContent value="history" className="mt-4" data-testid="contact-detail-content-history">
          {data.history.length === 0 ? (
            <div className="crm-card p-8 text-center text-sm text-gray-500">
              Noch keine Einträge in der Historie.
            </div>
          ) : (
            <div className="crm-card p-2 divide-y divide-gray-100">
              {data.history.map((h) => {
                const meta = KIND_META[h.kind] || KIND_META.activity;
                const Icon = meta.icon;
                return (
                  <div key={`${h.kind}-${h.id}`} className="flex items-start gap-3 p-3">
                    <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${meta.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-gray-900 truncate">{h.title}</div>
                        <div className="text-xs text-gray-500 shrink-0">{fmtDateTime(h.occurred_at)}</div>
                      </div>
                      {h.description ? (
                        <p className="text-sm text-gray-600 mt-0.5 line-clamp-3">{h.description}</p>
                      ) : null}
                      <div className="text-[11px] uppercase tracking-wider text-gray-400 mt-1">
                        {meta.label}
                        {h.meta ? ` · ${ART_LABEL[h.meta] || h.meta}` : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Details */}
        <TabsContent value="details" className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-detail-content-details">
          <Section title="Kontakt">
            <dl className="text-sm space-y-1.5">
              <Row label="Vorname" value={c.first_name} />
              <Row label="Nachname" value={c.last_name} />
              <Row label="Position" value={c.position} />
              <Row label="E-Mail" value={c.email} />
              <Row label="Telefon" value={c.phone} />
              <Row label="Mobil" value={c.mobile} />
              <Row label="Firma" value={c.company_name} />
            </dl>
          </Section>
          <WorkAddressSection contact={c} />
          <Section title="Freie Notizen">
            {c.notes ? (
              <p className="text-sm text-gray-800 whitespace-pre-line">{c.notes}</p>
            ) : (
              <EmptyLine>Keine freien Notizen hinterlegt.</EmptyLine>
            )}
          </Section>
        </TabsContent>
      </Tabs>

      <ConversationLogDialog
        open={convOpen}
        onOpenChange={setConvOpen}
        contactId={contactId}
        contactName={`${c.first_name} ${c.last_name}`}
        challenges={[...activeChallenges, ...otherChallenges]}
        onSaved={() => qc.invalidateQueries({ queryKey: ["contact-dossier", contactId] })}
      />

      <ChallengeDialog
        open={challengeOpen}
        onOpenChange={setChallengeOpen}
        defaultContactIds={[contactId]}
        onSaved={() => qc.invalidateQueries({ queryKey: ["contact-dossier", contactId] })}
      />

      <ContactEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        contact={c}
        onSaved={() => qc.invalidateQueries({ queryKey: ["contact-dossier", contactId] })}
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start gap-3">
      <dt className="w-24 text-[11px] uppercase tracking-wider text-gray-500 pt-0.5 shrink-0">{label}</dt>
      <dd className="text-sm text-gray-800 min-w-0 flex-1">
        {value || <span className="text-gray-400 italic">—</span>}
      </dd>
    </div>
  );
}

function ChallengeCard({ challenge }) {
  const status = CHALLENGE_STATUS[challenge.status] || CHALLENGE_STATUS.aktiv;
  const prio = PRIO[challenge.prioritaet] || PRIO.mittel;
  const needsDate = (challenge.status === "aktiv" || challenge.status === "ruhend") && !challenge.naechster_termin;
  return (
    <div className="crm-card p-4" data-testid={`contact-detail-challenge-${challenge.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-gray-900">{challenge.titel}</div>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border ${status.color}`}>
              {status.label}
            </span>
            <span className={`text-[10px] uppercase tracking-wider font-semibold ${prio.color}`}>
              Prio: {prio.label}
            </span>
          </div>
          {challenge.ziel ? <p className="text-sm text-gray-600 mt-1">{challenge.ziel}</p> : null}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Letzter Schritt</div>
          <div className="text-gray-700">{challenge.letzter_schritt || <span className="text-gray-400 italic">—</span>}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Nächster Schritt</div>
          <div className="text-gray-700">{challenge.naechster_schritt || <span className="text-gray-400 italic">—</span>}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Nächster Termin</div>
          <div className={`${needsDate ? "text-amber-700" : "text-gray-700"}`}>
            {challenge.naechster_termin ? fmtDateTime(challenge.naechster_termin) : <span className="italic">—</span>}
          </div>
          {needsDate ? (
            <div className="text-[11px] text-amber-700 mt-0.5" data-testid="challenge-missing-date-hint">
              Freundlicher Hinweis: aktive/ruhende Herausforderungen sollten einen Termin haben.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}


function FollowUpLink({ openFu, hasNext }) {
  const navigate = useNavigate();
  if (openFu) {
    return (
      <button
        type="button"
        onClick={() => navigate(`/followups?open=${openFu.id}`)}
        className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-800 hover:underline"
        data-testid="contact-detail-open-followup"
      >
        <ExternalLink className="w-3.5 h-3.5" />
        Wiedervorlage öffnen · {formatDeDate(openFu.due_date)}
      </button>
    );
  }
  if (hasNext) {
    return (
      <p className="mt-3 text-xs text-amber-700" data-testid="contact-detail-followup-missing">
        Hinweis: Für diesen nächsten Schritt existiert noch keine Wiedervorlage. Beim nächsten Speichern
        wird sie automatisch angelegt.
      </p>
    );
  }
  return null;
}

function WorkAddressSection({ contact }) {
  const hasCompany = Boolean(contact?.company_id);
  const hasWorkAddr =
    contact?.company_street ||
    contact?.company_postal_code ||
    contact?.company_city ||
    contact?.company_country;
  if (!hasCompany) return null;

  const line1 = [contact.company_street, contact.company_house_number].filter(Boolean).join(" ").trim();
  const line2 = [contact.company_postal_code, contact.company_city].filter(Boolean).join(" ").trim();
  const line3 = contact.company_country || "";

  return (
    <Section title="Arbeitsadresse" testid="contact-detail-work-address">
      <div className="text-sm text-gray-800">
        <div className="text-xs text-gray-500 mb-1">
          Übernommen aus Firma <span className="font-medium text-gray-700">{contact.company_name}</span>
        </div>
        {hasWorkAddr ? (
          <div className="leading-relaxed" data-testid="contact-detail-work-address-lines">
            {line1 ? <div>{line1}</div> : null}
            {line2 ? <div>{line2}</div> : null}
            {line3 ? <div>{line3}</div> : null}
          </div>
        ) : (
          <EmptyLine>Für diese Firma ist noch keine Adresse hinterlegt.</EmptyLine>
        )}
        <p className="text-[11px] text-gray-500 mt-2">
          Änderungen erfolgen zentral in der Firmenakte. Eine private Adresse bleibt hiervon unberührt.
        </p>
      </div>
    </Section>
  );
}

